/**
 * FileUploadField.tsx
 *
 * File upload field component (primarily for PDFs)
 */

import React from 'react';
import { BaseFieldProps, BASE_INPUT_CLASSES, ERROR_INPUT_CLASSES } from './shared/types';
import { FieldWrapper } from './shared';
import { Label } from '../ui/label';
import { X } from 'lucide-react';
import { Button } from '../ui/button';

export const FileUploadField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPreviewMode) return;

    const file = e.target.files?.[0];
    if (file) {
      onChange(fieldConfig.id, file);
    }
  };

  const inputClasses = `${BASE_INPUT_CLASSES} ${hasError ? ERROR_INPUT_CLASSES : ''}`;

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      showLabel={false}
      isPreview={isPreviewMode}
    >
      {/* Hide inline label in preview mode since it's shown in the field header */}
      {!isPreviewMode && (
        <Label htmlFor={fieldConfig.id}>
          {fieldConfig.label}{' '}
          {fieldConfig.required && <span className="text-red-500">*</span>}
        </Label>
      )}

      <div className="flex flex-col gap-2">
        <input
          type="file"
          id={fieldConfig.id}
          accept="application/pdf"
          onChange={handleChange}
          disabled={isDisabled}
          className={inputClasses}
        />

        {/* Display preview for File objects */}
        {value && value instanceof File && (
          <div className="mt-2 p-4 border border-gray-300 rounded bg-gray-50">
            <p className="text-sm text-gray-600">
              File: {value.name} ({(value.size / 1024).toFixed(1)} KB)
            </p>
            <p className="text-xs text-gray-500">
              Type: {value.type || 'Unknown'}
            </p>
          </div>
        )}

        {/* Display saved file indicator for string values */}
        {typeof value === 'string' && value && (
          <div className="mt-2 p-4 border border-gray-300 rounded bg-gray-50">
            <p className="text-sm text-gray-600">Uploaded File: {value}</p>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
});

FileUploadField.displayName = 'FileUploadField';
